import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import useContact from "./useContact"; // Adjust path as needed
import { predefinedIcons } from "./utils/contactData";

function CreateContact({ onSetActiveTab, resetForm }) {
  const [selectedIcon, setSelectedIcon] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      link: "",
      type: "",
      image: null,
      iconSource: "",
    },
  });

  const { createContactFn, isCreatingContact } = useContact();

  const contactTypes = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "website", label: "Website" },
    { value: "social", label: "Social Media" },
    { value: "address", label: "Address" },
    { value: "other", label: "Other" },
  ];

  const selectedType = watch("type");

  // Reset form after successful creation
  useEffect(() => {
    if (resetForm) {
      reset();
      setSelectedIcon("");
      setSelectedImage(null);
    }
  }, [resetForm, reset]);

  // Clear opposite selection when user makes a choice
  const handleIconSelect = (iconId) => {
    setSelectedIcon(iconId);
    setSelectedImage(null);
    setValue("image", null);
  };

  const handleImageSelect = (file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPEG, PNG, GIF, or WebP)");
        return;
      }

      setSelectedImage(file);
      setSelectedIcon("");
      setValue("image", file);
    }
  };

  const onSubmit = (data) => {
    // Automatically determine iconSource based on user selection
    const iconSource = selectedIcon ? "material" : "custom";

    // Prepare the final data object
    const submitData = {
      title: data.title.trim(),
      link: data.link.trim(),
      type: data.type,
      iconSource: iconSource,
      // Include either selectedIcon or image based on choice
      ...(iconSource === "default"
        ? { selectedIcon: selectedIcon }
        : { image: selectedImage }),
    };

    // console.log("Submitting data:", submitData);

    createContactFn(submitData, {
      onSuccess: () => {
        reset();
        setSelectedIcon("");
        setSelectedImage(null);
      },
    });
  };

  // Check if user has made an icon/image selection
  const hasIconSelection = selectedIcon || selectedImage;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 bg-gray-50 p-4 rounded-lg border"
    >
      <div>
        <label className="block text-sm font-medium">Title</label>
        <input
          type="text"
          {...register("title", {
            required: "Title is required",
            minLength: {
              value: 2,
              message: "Title must be at least 2 characters",
            },
          })}
          className={`w-full border rounded p-2 ${
            errors.title ? "border-red-300" : ""
          }`}
          placeholder="Enter contact title"
        />
        {errors.title && (
          <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium">Type</label>
        <select
          {...register("type", { required: "Type is required" })}
          className={`w-full border rounded p-2 ${
            errors.type ? "border-red-300" : ""
          }`}
        >
          <option value="">Select contact type</option>
          {contactTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {errors.type && (
          <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium">Link</label>
        <input
          type="text"
          {...register("link", {
            required: "Link is required",
            validate: (value) => {
              if (selectedType === "email") {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return (
                  emailRegex.test(value) || "Please enter a valid email address"
                );
              }
              if (selectedType === "website") {
                const urlRegex =
                  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
                return urlRegex.test(value) || "Please enter a valid URL";
              }
              if (selectedType === "phone") {
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                return (
                  phoneRegex.test(value.replace(/\s/g, "")) ||
                  "Please enter a valid phone number"
                );
              }
              return true;
            },
          })}
          className={`w-full border rounded p-2 ${
            errors.link ? "border-red-300" : ""
          }`}
          placeholder={
            selectedType === "email"
              ? "Enter email address"
              : selectedType === "phone"
              ? "Enter phone number"
              : selectedType === "website"
              ? "Enter website URL"
              : "Enter contact link"
          }
        />
        {errors.link && (
          <p className="text-red-500 text-sm mt-1">{errors.link.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Choose Icon{" "}
          {!hasIconSelection && <span className="text-red-500">*</span>}
        </label>
        <div className="grid grid-cols-5 gap-2">
          {predefinedIcons.map((icon) => (
            <button
              key={icon.id}
              type="button"
              className={`p-3 border rounded text-center hover:bg-gray-100 transition-colors ${
                selectedIcon === icon.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300"
              } flex flex-col items-center justify-center`}
              onClick={() => handleIconSelect(icon.id)}
            >
              <div className="text-2xl mb-1">{icon.preview}</div>
              <div className="text-xs text-gray-600">{icon.label}</div>
            </button>
          ))}
        </div>
        {selectedIcon && (
          <p className="text-sm text-green-600 mt-2">
            Selected Icon:{" "}
            {predefinedIcons.find((i) => i.id === selectedIcon)?.label}
          </p>
        )}
      </div>

      <div className="text-center text-sm text-gray-500 font-medium">OR</div>

      <div>
        <label className="block text-sm font-medium">
          Upload Custom Image{" "}
          {!hasIconSelection && <span className="text-red-500">*</span>}
        </label>
        <input
          type="file"
          accept="image/*"
          className="w-full border rounded p-2"
          onChange={(e) => {
            const file = e.target.files[0];
            handleImageSelect(file);
          }}
        />
        {selectedImage && (
          <div className="mt-2">
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Preview"
              className="w-20 h-20 object-cover rounded border"
            />
            <p className="text-sm text-green-600 mt-1">
              Selected Image: {selectedImage.name}
            </p>
          </div>
        )}
      </div>

      {!hasIconSelection && (
        <p className="text-red-500 text-sm">
          Please select an icon or upload a custom image
        </p>
      )}

      <div className="flex gap-4 justify-end">
        <button
          type="button"
          className="px-4 py-2 rounded bg-gray-200"
          onClick={() => onSetActiveTab("list")}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white"
          disabled={isCreatingContact || !hasIconSelection}
        >
          {isCreatingContact ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}

export default CreateContact;
